import S from './core.js';
import { HEADER_FIELDS } from './constants.js';

/* Set by the sign-in layer (auth/AuthGate.jsx) so every API call carries the user's Microsoft Entra access token. */
let getToken = null;
export const setTokenProvider = (fn) => { getToken = fn; };

async function request(path, options = {}) {
  const token = getToken ? await getToken() : null;
  const headers = token ? { ...options.headers, Authorization: `Bearer ${token}` } : options.headers;
  const res = await fetch('/api' + path, { ...options, headers });
  if (!res.ok) {
    let message = res.statusText;
    try {
      const body = await res.json();
      message = typeof body.detail === 'string' ? body.detail : JSON.stringify(body.detail);
    } catch { /* not JSON */ }
    throw Object.assign(new Error(message), { status: res.status });
  }
  return res.status === 204 ? null : res.json();
}

const path = (k) => `/reports/${encodeURIComponent(k.id)}/${k.year}/${k.asAt}`;
export const keyOfSummary = (r) => ({ id: r.settlement_report_id, year: r.report_year, asAt: r.as_at_date });

/* ---- live backend ---- */
const live = {
  listReports: () => request('/reports'),
  getReport: (k) => request(path(k)),
  saveReport: (k, body) => request(path(k), { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }),
  deleteReport: (k) => request(path(k), { method: 'DELETE' }),
  approveReport: (k, approved) => request(`${path(k)}/approve`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ approved }) }),
};

/* ---- demo mode: no backend needed ----
 * REACT_APP_DEMO=true  -> always demo      REACT_APP_DEMO=false -> always live
 * unset                -> use the backend if /api/health answers, otherwise fall back to the demo data */
let mode = 'live';
let demo = null;
export const getMode = () => mode;

async function backendAvailable() {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), 3000);
  try {
    const res = await fetch('/api/health', { signal: ctrl.signal });
    if (!res.ok) return false;
    const body = await res.json();                    // a static host may answer with index.html: that is not the API
    return !!body && body.status === 'ok';
  } catch { return false; } finally { clearTimeout(timer); }
}
export async function detectMode() {
  const flag = process.env.REACT_APP_DEMO;
  mode = flag === 'true' ? 'demo' : flag === 'false' ? 'live' : (await backendAvailable()) ? 'live' : 'demo';
  if (mode === 'demo' && !demo) demo = await import('./demo/demoApi.js');
  return mode;
}
const impl = () => (mode === 'demo' ? demo : live);
export const listReports = () => impl().listReports();
export const getReport = (k) => impl().getReport(k);
export const saveReport = (k, body) => impl().saveReport(k, body);
export const deleteReport = (k) => impl().deleteReport(k);
export const approveReport = (k, approved) => impl().approveReport(k, approved);
export const resetDemo = () => (demo ? demo.resetDemo() : undefined);

/* API payload <-> the editing model used by the engine */
export function toModel(res) {
  const { extra_fields, ...report } = res.report;
  report.extra = extra_fields || {};
  const figures = S.calculateTotals(res.figures.map((f) => ({ ...f })), report);    // quarters and the year are calculated from the months, not read
  return { report, figures, revision: res.revision, origReport: S.clone(report), origFigures: S.clone(figures) };
}
export function toBody(model) {
  const r = model.report;
  const report = { as_at_date: r.as_at_date, extra_fields: r.extra };
  HEADER_FIELDS.forEach(([k]) => { report[k] = r[k] ?? null; });
  return { report, figures: model.figures.map(({ vstr, ...f }) => f), revision: model.revision };
}
